import type { Point, TextStyle, Bounds, HandlePosition, Handle } from '../state/types';
import { DEFAULT_TEXT_STYLE } from '../state/types';
import {
  renderFormattedTextLines,
  resolveTextAlignPosition,
  wrapText,
} from '../tools/textLayout';
import { CLIPBOARD, COLORS, LINE_HEIGHT_RATIO, PADDING } from '../../../config/constants';
import { getViewportScale } from './selectionRender';
import { generateId } from '../../../utils/id';
import { buildElementJSON } from '../state/elementSerialization';
import { parseTextLine, getFontSizeMultiplier } from '../textEditor/TextFormatter';

export class TextElement {
  readonly id: string;
  position: Point;
  text: string;
  textStyle: TextStyle;
  selected: boolean;
  parentShapeId?: string; // Optional: attach text to a shape in draw mode
  maxWidth?: number; // Optional: max width for text wrapping (used when attached to shape)
  autoResize: boolean; // When true, text grows horizontally (no wrapping). When false, wraps at maxWidth.
  private cachedBounds: Bounds | null = null;
  private cachedText: string = '';
  private cachedFontSize: number = 0;
  private cachedFontWeight: TextStyle['fontWeight'] = 'normal';
  private cachedFontStyle: TextStyle['fontStyle'] = 'normal';
  private cachedFontFamily: string = '';
  private cachedMaxWidth: number | undefined = undefined;
  private resizeOriginFontSize: number | null = null;
  private resizeOriginMaxWidth: number | undefined = undefined;
  private resizeOriginWidth: number | null = null;
  private resizeAccumDelta: Point = { x: 0, y: 0 };

  constructor(position: Point, text = 'Text', style?: Partial<TextStyle>) {
    this.id = generateId('text');
    this.position = { ...position };
    this.text = text;
    this.textStyle = { ...DEFAULT_TEXT_STYLE, ...style };
    this.selected = false;
    this.autoResize = true;
  }

  getBounds(ctx: CanvasRenderingContext2D): Bounds {
    // Use cached bounds if text and fontSize haven't changed
    if (
      this.cachedBounds &&
      this.cachedText === this.text &&
      this.cachedFontSize === this.textStyle.fontSize &&
      this.cachedFontWeight === this.textStyle.fontWeight &&
      this.cachedFontStyle === this.textStyle.fontStyle &&
      this.cachedFontFamily === this.textStyle.fontFamily &&
      this.cachedMaxWidth === this.maxWidth
    ) {
      return {
        x: this.position.x,
        y: this.position.y,
        width: this.cachedBounds.width,
        height: this.cachedBounds.height,
      };
    }

    const { fontSize, fontWeight, fontStyle, fontFamily } = this.textStyle;

    // For empty text, return minimal bounds
    if (!this.text || this.text.trim() === '') {
      const padding = PADDING.textElement;
      const lineHeight = fontSize * LINE_HEIGHT_RATIO;

      this.cachedBounds = {
        x: this.position.x,
        y: this.position.y,
        width: padding * 2,
        height: lineHeight + padding * 2,
      };
      this.cachedText = this.text;
      this.cachedFontSize = fontSize;
      this.cachedFontWeight = fontWeight;
      this.cachedFontStyle = fontStyle;
      this.cachedFontFamily = fontFamily;

      return this.cachedBounds;
    }

    // Parse lines to find max size multiplier for accurate bounds
    const padding = PADDING.textElement;
    let rawLines = this.text.split('\n');

    // If maxWidth is set (text attached to shape), wrap the text
    let allLines: string[] = [];
    if (this.maxWidth && this.maxWidth > padding * 2) {
      const availableWidth = this.maxWidth - padding * 2;

      for (const line of rawLines) {
        const format = parseTextLine(line);
        const multiplier = getFontSizeMultiplier(format.type);
        const adjustedFontSize = fontSize * multiplier;
        const adjustedFont = `${fontStyle} ${fontWeight} ${adjustedFontSize}px ${fontFamily}`;
        const wrappedLines = wrapText(ctx, format.content, availableWidth, adjustedFont);

        // Preserve formatting markers for bounds calculation
        const prefix = line.match(/^\s*(?:#{1,3}|[-*]|\d+\.)\s+/)?.[0] ?? '';
        for (const wrappedLine of wrappedLines) {
          allLines.push(prefix ? `${prefix}${wrappedLine}` : wrappedLine);
        }
      }
    } else {
      allLines = rawLines;
    }

    let maxMultiplier = 1;
    let totalHeight = 0;
    let maxWidth = 0;

    for (const line of allLines) {
      const format = parseTextLine(line);
      const multiplier = getFontSizeMultiplier(format.type);
      maxMultiplier = Math.max(maxMultiplier, multiplier);

      const adjustedFontSize = fontSize * multiplier;
      const lineHeight = adjustedFontSize * LINE_HEIGHT_RATIO;

      // Measure this line with its specific font size
      ctx.font = `${fontStyle} ${fontWeight} ${adjustedFontSize}px ${fontFamily}`;
      const textMetrics = ctx.measureText(format.content);

      // Use actual bounding box width when available for more accurate bounds;
      // measureText().width is the advance width which can understate visual width
      let lineWidth = textMetrics.actualBoundingBoxRight !== undefined
        ? textMetrics.actualBoundingBoxLeft + textMetrics.actualBoundingBoxRight
        : textMetrics.width;

      // Account for indent offset applied by renderFormattedTextLines
      lineWidth += format.indent * 12;

      // Add width for list markers
      if (format.type === 'unordered') {
        lineWidth += adjustedFontSize * 0.8; // Bullet + space
      } else if (format.type === 'ordered' && format.listNumber !== undefined) {
        const numberText = `${format.listNumber}.`;
        const numberWidth = ctx.measureText(numberText).width;
        lineWidth += numberWidth + adjustedFontSize * 0.4; // Number + space
      }

      maxWidth = Math.max(maxWidth, lineWidth);
      totalHeight += lineHeight;
    }

    // If maxWidth is set, use it as the bounds width
    const boundsWidth = this.maxWidth ? this.maxWidth : maxWidth + padding * 2;

    this.cachedBounds = {
      x: this.position.x,
      y: this.position.y,
      width: boundsWidth,
      height: totalHeight + padding * 2,
    };
    this.cachedText = this.text;
    this.cachedFontSize = fontSize;
    this.cachedFontWeight = fontWeight;
    this.cachedFontStyle = fontStyle;
    this.cachedFontFamily = fontFamily;
    this.cachedMaxWidth = this.maxWidth;

    return this.cachedBounds;
  }

  containsPoint(p: Point, ctx: CanvasRenderingContext2D): boolean {
    const bounds = this.getBounds(ctx);
    return (
      p.x >= bounds.x &&
      p.x <= bounds.x + bounds.width &&
      p.y >= bounds.y &&
      p.y <= bounds.y + bounds.height
    );
  }

  move(delta: Point): void {
    this.position.x += delta.x;
    this.position.y += delta.y;
  }

  /** Selection box padding — shared between handles and rendering. */
  private static readonly SEL_PAD = 6;

  getHandles(ctx: CanvasRenderingContext2D): Handle[] {
    const bounds = this.getBounds(ctx);
    const pad = TextElement.SEL_PAD;
    // Place handles at the padded bounding box edges, not the text bounds
    const x = bounds.x - pad;
    const y = bounds.y - pad;
    const w = bounds.width + pad * 2;
    const h = bounds.height + pad * 2;
    const midY = y + h / 2;

    return [
      { position: 'top-left', point: { x, y } },
      { position: 'top-right', point: { x: x + w, y } },
      { position: 'bottom-right', point: { x: x + w, y: y + h } },
      { position: 'bottom-left', point: { x, y: y + h } },
      { position: 'left', point: { x, y: midY } },
      { position: 'right', point: { x: x + w, y: midY } },
    ];
  }

  /** Returns only left/right handles (for edge bar rendering). */
  getVisibleHandles(ctx: CanvasRenderingContext2D): Handle[] {
    return this.getHandles(ctx).filter(
      h => h.position === 'left' || h.position === 'right'
    );
  }

  getHandleAtPoint(p: Point, ctx: CanvasRenderingContext2D, scale = 1): HandlePosition | null {
    const handles = this.getHandles(ctx);

    // Edge handles (left/right bars) get a tall but narrow zone
    const edgeThresholdX = 16 / scale;
    const edgeThresholdY = 28 / scale;
    // Corner handles get a generous hit zone extending outward
    const cornerThreshold = 20 / scale;

    // Check edge handles first — on small boxes, corner zones overlap edge zones
    // and edges should win since they're the more specific (narrower) target
    for (const handle of handles) {
      const isEdge = handle.position === 'left' || handle.position === 'right';
      if (!isEdge) continue;
      const dx = Math.abs(p.x - handle.point.x);
      const dy = Math.abs(p.y - handle.point.y);
      if (dx <= edgeThresholdX && dy <= edgeThresholdY) {
        return handle.position;
      }
    }

    for (const handle of handles) {
      const isCorner = handle.position.includes('-');
      if (!isCorner) continue;
      const dx = Math.abs(p.x - handle.point.x);
      const dy = Math.abs(p.y - handle.point.y);
      if (dx <= cornerThreshold && dy <= cornerThreshold) {
        return handle.position;
      }
    }

    return null;
  }

  startResize(ctx: CanvasRenderingContext2D): void {
    const bounds = this.getBounds(ctx);
    this.resizeOriginFontSize = this.textStyle.fontSize;
    this.resizeOriginMaxWidth = this.maxWidth;
    this.resizeOriginWidth = bounds.width;
    this.resizeAccumDelta = { x: 0, y: 0 };
  }

  endResize(): void {
    this.resizeOriginFontSize = null;
    this.resizeOriginMaxWidth = undefined;
    this.resizeOriginWidth = null;
    this.resizeAccumDelta = { x: 0, y: 0 };
  }

  resize(handle: HandlePosition, delta: Point, ctx: CanvasRenderingContext2D): void {
    // Accumulate delta from drag start for stable scaling
    this.resizeAccumDelta.x += delta.x;
    this.resizeAccumDelta.y += delta.y;

    const bounds = this.getBounds(ctx);

    // E/W handles: change width only, enable text wrapping
    if (handle === 'left' || handle === 'right') {
      this.autoResize = false;
      if (!this.maxWidth) {
        this.maxWidth = bounds.width;
      }

      if (handle === 'left') {
        this.position.x += delta.x;
        this.maxWidth = Math.max(50, this.maxWidth - delta.x);
      } else {
        this.maxWidth = Math.max(50, this.maxWidth + delta.x);
      }

      this.cachedBounds = null;
      return;
    }

    // Corner handles: diagonal-only resize — scale both fontSize and width proportionally
    const isCorner = handle === 'top-left' || handle === 'top-right' ||
                     handle === 'bottom-left' || handle === 'bottom-right';

    if (isCorner) {
      const isTop = handle === 'top-left' || handle === 'top-right';
      const isLeft = handle === 'top-left' || handle === 'bottom-left';

      // Use accumulated delta from drag start for stable scaling (avoids rounding drift)
      const diagX = isLeft ? -this.resizeAccumDelta.x : this.resizeAccumDelta.x;
      const diagY = isTop ? -this.resizeAccumDelta.y : this.resizeAccumDelta.y;
      const diagDelta = (diagX + diagY) / 2;

      const originWidth = this.resizeOriginWidth ?? Math.max(1, bounds.width);
      const scale = Math.max(0.2, (originWidth + diagDelta) / originWidth);

      const originFontSize = this.resizeOriginFontSize ?? this.textStyle.fontSize;
      const newFontSize = Math.max(5, originFontSize * scale);
      this.textStyle.fontSize = Math.round(newFontSize);

      if (this.resizeOriginMaxWidth !== undefined) {
        this.maxWidth = Math.max(50, this.resizeOriginMaxWidth * scale);
      }

      // Recalculate bounds after changes to get actual new size
      this.cachedBounds = null;
      const newBounds = this.getBounds(ctx);
      const newWidth = newBounds.width;
      const newHeight = newBounds.height;
      if (isLeft) {
        this.position.x += bounds.width - newWidth;
      }
      if (isTop) {
        this.position.y += bounds.height - newHeight;
      }

      this.cachedBounds = null;
      return;
    }

    this.cachedBounds = null;
  }

  render(ctx: CanvasRenderingContext2D): void {
    const { fontSize, fontWeight, fontStyle, textAlign, color, fontFamily } = this.textStyle;

    const bounds = this.getBounds(ctx);

    ctx.fillStyle = color;
    ctx.textAlign = textAlign;
    ctx.textBaseline = 'alphabetic';

    const padding = PADDING.textElement;
    let allLines: string[] = [];

    // If maxWidth is set (text attached to shape), wrap the text
    if (this.maxWidth && this.maxWidth > padding * 2) {
      const availableWidth = this.maxWidth - padding * 2;
      const rawLines = this.text.split('\n');

      for (const line of rawLines) {
        const format = parseTextLine(line);
        const multiplier = getFontSizeMultiplier(format.type);
        const adjustedFontSize = fontSize * multiplier;
        const adjustedFont = `${fontStyle} ${fontWeight} ${adjustedFontSize}px ${fontFamily}`;

        // Reduce available width for list items to account for bullet/number
        let listAvailableWidth = availableWidth;
        if (format.type === 'unordered') {
          listAvailableWidth -= adjustedFontSize * 0.8; // Bullet space
        } else if (format.type === 'ordered' && format.listNumber !== undefined) {
          ctx.font = adjustedFont;
          const numberWidth = ctx.measureText(`${format.listNumber}.`).width;
          listAvailableWidth -= numberWidth + adjustedFontSize * 0.4; // Number space
        }

        const wrappedLines = wrapText(ctx, format.content, listAvailableWidth, adjustedFont);

        // Re-add formatting markers to wrapped lines so renderFormattedTextLines can parse them
        // Match headers (# ## ###), unordered lists (- *), or ordered lists (1. 2. etc)
        const prefix = line.match(/^\s*(?:#{1,3}|[-*]|\d+\.)\s+/)?.[0] ?? '';
        for (const wrappedLine of wrappedLines) {
          allLines.push(prefix ? `${prefix}${wrappedLine}` : wrappedLine);
        }
      }
    } else {
      allLines = this.text.split('\n');
    }

    const baseLineHeight = fontSize * LINE_HEIGHT_RATIO;

    const { textX } = resolveTextAlignPosition(
      textAlign,
      this.position.x + padding,
      this.position.x + bounds.width / 2,
      this.position.x + bounds.width - padding
    );

    // Compute vertical offset matching textarea text placement (Excalidraw approach)
    // With textBaseline='alphabetic', Y is the baseline position.
    // A textarea with line-height places the baseline at: ascent + (lineHeight - (ascent + descent)) / 2
    ctx.font = `${fontStyle} ${fontWeight} ${fontSize}px ${fontFamily}`;
    const metrics = ctx.measureText('M');
    const ascent = metrics.fontBoundingBoxAscent ?? metrics.actualBoundingBoxAscent ?? fontSize * 0.8;
    const descent = metrics.fontBoundingBoxDescent ?? metrics.actualBoundingBoxDescent ?? fontSize * 0.2;
    const lineGap = (baseLineHeight - ascent - descent) / 2;
    const verticalOffset = ascent + lineGap;

    const startY = this.position.y + padding + verticalOffset;

    renderFormattedTextLines(ctx, {
      lines: allLines,
      baseFontSize: fontSize,
      baseLineHeight,
      textX,
      startY,
      fontWeight,
      fontStyle,
      fontFamily,
    });
  }

  renderSelection(ctx: CanvasRenderingContext2D): void {
    if (!this.selected) return;
    if (!this.text || this.text.trim() === '') return;

    const scale = getViewportScale(ctx);
    const bounds = this.getBounds(ctx);
    const pad = TextElement.SEL_PAD / scale;
    const selColor = COLORS.textSelection;

    const bx = bounds.x - pad;
    const by = bounds.y - pad;
    const bw = bounds.width + pad * 2;
    const bh = bounds.height + pad * 2;

    ctx.save();
    ctx.strokeStyle = selColor;
    ctx.lineWidth = 1.5 / scale;
    ctx.lineCap = 'round';
    ctx.setLineDash([]);

    // Corner bracket length — scales with box size, clamped
    const arm = Math.min(12 / scale, Math.max(6 / scale, Math.min(bw, bh) * 0.2));

    // Draw L-shaped brackets at each corner
    // Top-left
    ctx.beginPath();
    ctx.moveTo(bx, by + arm);
    ctx.lineTo(bx, by);
    ctx.lineTo(bx + arm, by);
    ctx.stroke();

    // Top-right
    ctx.beginPath();
    ctx.moveTo(bx + bw - arm, by);
    ctx.lineTo(bx + bw, by);
    ctx.lineTo(bx + bw, by + arm);
    ctx.stroke();

    // Bottom-right
    ctx.beginPath();
    ctx.moveTo(bx + bw, by + bh - arm);
    ctx.lineTo(bx + bw, by + bh);
    ctx.lineTo(bx + bw - arm, by + bh);
    ctx.stroke();

    // Bottom-left
    ctx.beginPath();
    ctx.moveTo(bx + arm, by + bh);
    ctx.lineTo(bx, by + bh);
    ctx.lineTo(bx, by + bh - arm);
    ctx.stroke();

    // Left/right edge bars — vertical pills for width resize
    const visibleHandles = this.getVisibleHandles(ctx);
    const barWidth = 5 / scale;
    const barHeight = Math.min(24 / scale, Math.max(12 / scale, bh * 0.35));
    const barRadius = barWidth / 2;

    ctx.shadowColor = 'rgba(0, 0, 0, 0.12)';
    ctx.shadowBlur = 2 / scale;
    ctx.shadowOffsetY = 1 / scale;

    ctx.fillStyle = COLORS.white;
    ctx.strokeStyle = selColor;
    ctx.lineWidth = 1.5 / scale;

    for (const handle of visibleHandles) {
      const rx = handle.point.x - barWidth / 2;
      const ry = handle.point.y - barHeight / 2;

      ctx.beginPath();
      ctx.moveTo(rx + barRadius, ry);
      ctx.lineTo(rx + barWidth - barRadius, ry);
      ctx.arcTo(rx + barWidth, ry, rx + barWidth, ry + barRadius, barRadius);
      ctx.lineTo(rx + barWidth, ry + barHeight - barRadius);
      ctx.arcTo(rx + barWidth, ry + barHeight, rx + barWidth - barRadius, ry + barHeight, barRadius);
      ctx.lineTo(rx + barRadius, ry + barHeight);
      ctx.arcTo(rx, ry + barHeight, rx, ry + barHeight - barRadius, barRadius);
      ctx.lineTo(rx, ry + barRadius);
      ctx.arcTo(rx, ry, rx + barRadius, ry, barRadius);
      ctx.closePath();
      ctx.fill();
      ctx.stroke();
    }

    ctx.restore();
  }

  isAttachedToShape(): boolean {
    return this.parentShapeId !== undefined;
  }

  clone(): TextElement {
    const cloned = new TextElement(
      { x: this.position.x + CLIPBOARD.pasteOffset, y: this.position.y + CLIPBOARD.pasteOffset },
      this.text,
      { ...this.textStyle }
    );
    if (this.parentShapeId) {
      cloned.parentShapeId = this.parentShapeId;
    }
    return cloned;
  }

  toJSON(): object {
    const data: {
      position: Point;
      text: string;
      textStyle: TextStyle;
      parentShapeId?: string;
      maxWidth?: number;
      autoResize?: boolean;
    } = {
      position: this.position,
      text: this.text,
      textStyle: this.textStyle,
    };
    if (this.parentShapeId) {
      data.parentShapeId = this.parentShapeId;
    }
    if (this.maxWidth) {
      data.maxWidth = this.maxWidth;
    }
    if (!this.autoResize) {
      data.autoResize = false;
    }
    return buildElementJSON(this.id, 'text', data);
  }

  static fromJSON(data: {
    id?: string;
    position: Point;
    text: string;
    textStyle: TextStyle;
    parentShapeId?: string;
    maxWidth?: number;
    autoResize?: boolean;
  }): TextElement {
    const element = new TextElement(data.position, data.text, data.textStyle);
    if (data.id) {
      (element as { id: string }).id = data.id;
    }
    if (data.parentShapeId) {
      element.parentShapeId = data.parentShapeId;
    }
    if (data.maxWidth) {
      element.maxWidth = data.maxWidth;
    }
    if (data.autoResize === false) {
      element.autoResize = false;
    }
    return element;
  }
}
